import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { kgToLb } from "@/lib/units";
import api from "@/lib/api";

const DAY = 86400000;

const dataUrlToFile = async (dataUrl, filename) => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: "image/png" });
};

const persistSnapshot = async (file, kind, metrics) => {
  try {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    if (metrics) fd.append("metrics_json", JSON.stringify(metrics));
    await api.post("/snapshots", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (e) {
    console.warn("snapshot save failed", e);
  }
};

/**
 * Personal progress snapshot for clients to share on social.
 * Computes last-30-day metrics from the data the Dashboard already loads:
 *   - workouts completed
 *   - total volume lifted
 *   - top PR
 *   - weight change
 * Renders a 1080×1080 PNG, anonymous (no email, no coach name).
 */
export default function ClientShareSnapshot({
  firstName = "Athlete",
  stats,
  weights = [],
  isImperial = false,
}) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const cutoff = Date.now() - 30 * DAY;
  const recentVol = (stats?.volume_trend || []).filter(
    (p) => new Date(p.date).getTime() >= cutoff,
  );
  const workoutsLast30 = recentVol.length;
  const totalVolKg = recentVol.reduce((sum, p) => sum + Number(p.volume || 0), 0);
  const totalVolDisplay = isImperial
    ? Math.round(kgToLb(totalVolKg)).toLocaleString()
    : Math.round(totalVolKg).toLocaleString();
  const volUnit = isImperial ? "lbs" : "kg";

  // Top PR (heaviest single set across all exercises)
  const prs = stats?.personal_records || {};
  let topPrName = null;
  let topPrKg = 0;
  for (const [name, kg] of Object.entries(prs)) {
    if (Number(kg) > topPrKg) {
      topPrKg = Number(kg);
      topPrName = name;
    }
  }
  const topPrDisplay = topPrKg
    ? `${isImperial ? (Math.round(kgToLb(topPrKg) * 2) / 2).toFixed(1) : topPrKg.toFixed(1)} ${volUnit}`
    : "—";

  // Weight delta over the last 30 days
  const recentWeights = weights
    .filter((w) => new Date(w.created_at).getTime() >= cutoff)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  let weightDeltaTxt = "—";
  if (recentWeights.length >= 2) {
    const first = recentWeights[0].weight_kg;
    const last = recentWeights[recentWeights.length - 1].weight_kg;
    const deltaKg = last - first;
    const display = isImperial ? kgToLb(deltaKg) : deltaKg;
    const sign = display > 0 ? "+" : "";
    weightDeltaTxt = `${sign}${display.toFixed(1)} ${volUnit}`;
  }

  const capture = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0b0b0c",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const filename = `ripple-effect-30day-${new Date().toISOString().slice(0, 10)}.png`;
      const file = await dataUrlToFile(dataUrl, filename);

      persistSnapshot(file, "client", {
        workouts_last_30: workoutsLast30,
        total_volume_kg: totalVolKg,
        top_pr_kg: topPrKg,
        top_pr_name: topPrName,
        weight_delta_txt: weightDeltaTxt,
      });

      if (
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        try {
          await navigator.share({
            files: [file],
            title: "My 30-day Ripple Effect progress",
            text: `Logged ${workoutsLast30} workouts and moved ${totalVolDisplay} ${volUnit}. rippleprogress.com`,
          });
          toast.success("Shared");
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Snapshot downloaded");
    } catch (e) {
      toast.error("Could not generate snapshot");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="client-share-snapshot-btn"
        onClick={capture}
        disabled={busy}
        className="ghost-btn px-3 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50 w-full justify-center"
        title="Download a sharable PNG of your 30-day progress"
      >
        {busy ? <Download className="h-3.5 w-3.5 animate-pulse" /> : <Share2 className="h-3.5 w-3.5" />}
        {busy ? "Rendering…" : "Share my 30 days"}
      </button>

      <div
        ref={cardRef}
        data-testid="client-share-snapshot-canvas"
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: 1080,
          height: 1080,
          background: "#0b0b0c",
          color: "#e7e7e7",
          fontFamily: "Manrope, Arial, sans-serif",
          padding: 72,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 56 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src="/icon-192.png" alt="" width={56} height={56} style={{ borderRadius: 4 }} />
            <div>
              <div style={{ fontSize: 13, letterSpacing: 4, color: "#FF3B30", textTransform: "uppercase" }}>/ 30-day report</div>
              <div style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 44, lineHeight: 1, letterSpacing: 2, marginTop: 8 }}>
                {firstName.toUpperCase()}, ON THE MOVE.
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, letterSpacing: 3, color: "#7a7a7d", textTransform: "uppercase" }}>
            {new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>

        <div style={{ fontSize: 22, color: "#b8b8bb", lineHeight: 1.35, marginBottom: 56, maxWidth: 820 }}>
          The last 30 days, by the numbers. Reps logged. Iron moved. Body changing.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <Tile label="Workouts completed" value={workoutsLast30} sub="Sessions logged" highlight />
          <Tile
            label={`Total volume (${volUnit})`}
            value={totalVolDisplay}
            sub="Every rep × every plate"
          />
          <Tile label="Heaviest PR" value={topPrDisplay} sub={topPrName || "Log a heavy single"} />
          <Tile label="Weight change" value={weightDeltaTxt} sub="vs 30 days ago" />
        </div>

        <div
          style={{
            marginTop: 72,
            paddingTop: 28,
            borderTop: "1px solid #2a2a2d",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 16, color: "#b8b8bb" }}>
            Train smarter with AI coaching.
            <span style={{ color: "#FF3B30", marginLeft: 12, fontWeight: 700 }}>rippleprogress.com</span>
          </div>
          <div style={{ fontSize: 11, color: "#7a7a7d", letterSpacing: 3, textTransform: "uppercase" }}>
            #RippleEffect
          </div>
        </div>
      </div>
    </>
  );
}

function Tile({ label, value, sub, highlight }) {
  return (
    <div
      style={{
        background: "#141416",
        border: "1px solid #2a2a2d",
        padding: 28,
        borderRadius: 4,
      }}
    >
      <div style={{ fontSize: 12, color: "#7a7a7d", letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 58,
          lineHeight: 1,
          color: highlight ? "#FF3B30" : "#ffffff",
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 13, color: "#7a7a7d", marginTop: 12, textTransform: "uppercase", letterSpacing: 2 }}>
        {sub}
      </div>
    </div>
  );
}
